import { useEffect, useState } from "react";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import VehicleService from "../../services/VehicleService/VehicleService.ts";
import {
  findKeyByValue,
  normalizeDate,
  normalizeVehiclePolicyValues,
  normalizeVehicleProperties,
} from "../../utils/NormalizationUtil.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { VehicleDataType } from "../../types/vehicle.ts";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import VehiclePoliciesDataGrid from "../../components/DataTables/VehiclePoliciesDataGrid.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const VehicleData = () => {
  const [vehicleData, setVehicleData] = useState<VehicleDataType>({});
  const [vehicleTypes, setVehicleTypes] = useState<{ [key: string]: string }>(
    {}
  );
  const [usages, setUsages] = useState<{ [key: string]: string }>({});
  const [engineTypes] = useState<{ [key: string]: string }>({
    B: "Бензин",
    D: "Дизел",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">(
    "success"
  );
  const [vehiclePolicies, setVehiclePolicies] = useState<
    { [key: string]: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const { jwt } = useAuth();

  const displayAlert = (
    message: string,
    type: "success" | "error" | "warning"
  ) => {
    setAlert(message);
    setAlertType(type);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [typesResult, usagesResult] = await Promise.all([
          VehicleService.getVehicleTypes(jwt),
          VehicleService.getVehicleUsages(jwt),
        ]);
        setVehicleTypes(typesResult as { [key: string]: string });
        setUsages(usagesResult as { [key: string]: string });
      } catch (error) {
        displayAlert("Грешка при зареждане на данни за МПС.", "error");
      }
    };
    fetchInitialData();
  }, [jwt]);

  const getVehicleDataByRegistrationNumber = async (e: any) => {
    e.preventDefault();
    if (
      !vehicleData.vehicleRegistrationNumber &&
      !vehicleData.vehicleCertificate
    )
      return;
    try {
      const vehicle = await VehicleService.getVehicle(
        vehicleData.vehicleRegistrationNumber,
        vehicleData.vehicleCertificate,
        jwt
      );
      const normalizedVehicleData = normalizeVehicleProperties(
        vehicle as object
      );

      if (Object.values(normalizedVehicleData).length === 0) {
        displayAlert(
          `Няма намерени данни за МПС с номер ${
            vehicleData.vehicleRegistrationNumber ??
            vehicleData.vehicleCertificate
          }`,
          FadeAlertType.warning
        );
        return;
      }

      const resolved = {
        ...normalizedVehicleData,
        vehicleUsage: findKeyByValue(
          usages,
          normalizedVehicleData.vehicleUsage ?? ""
        ),
        vehicleType: findKeyByValue(
          vehicleTypes,
          normalizedVehicleData.vehicleType ?? ""
        ),
      };
      setVehicleData(resolved);
      displayAlert("Данните са успешно заредени", FadeAlertType.success);
    } catch (error) {
      displayAlert("Грешка при зареждане на МПС", FadeAlertType.error);
    }
  };

  const saveVehicle = async (e: any) => {
    e.preventDefault();
    try {
      const normalizedVehicleData = normalizeVehicleProperties(
        vehicleData,
        true
      );
      const response = await VehicleService.saveVehicle(
        jwt,
        normalizedVehicleData
      );
      if (Object.values(response).length === 0) {
        displayAlert("Грешка при записването", FadeAlertType.error);
      } else {
        displayAlert("Успешен запис", FadeAlertType.success);
        setVehicleData({});
        setVehiclePolicies([]);
      }
    } catch (e: any) {
      displayAlert(e.message, FadeAlertType.error);
    }
  };

  const getVehiclePoliciesByRegistrationNumber = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    const vehiclePolicies = await VehicleService.getVehiclePolicies(
      vehicleData.vehicleRegistrationNumber,
      vehicleData.vehicleCertificate,
      jwt
    );
    const normalizedVehiclePolicies = (vehiclePolicies as []).map((policy) =>
      normalizeVehiclePolicyValues(policy)
    );
    const normalizedPoliciesWithDates = normalizedVehiclePolicies.map(
      (policy) => ({
        ...policy,
        fromDate: normalizeDate(policy.fromDate),
        toDate: normalizeDate(policy.toDate),
        endDate: normalizeDate(policy.endDate),
      })
    );
    setVehiclePolicies(normalizedPoliciesWithDates);
    setIsLoading(false);
  };

  return (
    <>
      <Breadcrumb pageName="Данни за МПС" />

      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <SectionHeader title="Данни за МПС" />

        <div className="mt-2 p-3 pt-1">
          {/* Row 1 - 5 identification fields */}
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div>
              <label className={labelClass}>Рег. номер</label>
              <input
                type="text"
                value={vehicleData.vehicleRegistrationNumber || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleRegistrationNumber: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>№ Талон</label>
              <input
                type="text"
                value={vehicleData.vehicleCertificate || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleCertificate: e.target.value,
                  })
                }
                onBlur={getVehicleDataByRegistrationNumber}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>№ Рама</label>
              <input
                type="text"
                value={vehicleData.chassisNumber || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    chassisNumber: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Вид МПС</label>
              <SelectGroup
                options={vehicleTypes}
                value={vehicleData.vehicleType || ""}
                onChange={(value) =>
                  setVehicleData({ ...vehicleData, vehicleType: value })
                }
              />
            </div>
            <div>
              <label className={labelClass}>Марка</label>
              <input
                type="text"
                value={vehicleData.vehicleBrand || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleBrand: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 2 - 5 engine/spec fields */}
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div>
              <label className={labelClass}>Модел</label>
              <input
                type="text"
                value={vehicleData.vehicleModel || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleModel: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>ДВГ Мощност</label>
              <input
                type="number"
                value={vehicleData.engineCapacity || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    engineCapacity: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>ДВГ куб.см</label>
              <input
                type="text"
                value={vehicleData.vehicleVolume || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleVolume: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Тип ДВГ</label>
              <SelectGroup
                options={engineTypes}
                value={vehicleData.engineType || ""}
                onChange={(value) =>
                  setVehicleData({ ...vehicleData, engineType: value })
                }
              />
            </div>
            <div>
              <label className={labelClass}>Ползване</label>
              <SelectGroup
                options={usages}
                value={vehicleData.vehicleUsage || ""}
                onChange={(value) =>
                  setVehicleData({ ...vehicleData, vehicleUsage: value })
                }
              />
            </div>
          </div>

          {/* Row 3 - year + other data */}
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div>
              <label className={labelClass}>Година произв.</label>
              <input
                type="number"
                value={vehicleData.manufactureYear || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    manufactureYear: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>
            <div className="col-span-2 lg:col-span-4">
              <label className={labelClass}>Други данни</label>
              <textarea
                data-no-decimal-guard=""
                value={vehicleData.vehicleOtherData || ""}
                onChange={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    vehicleOtherData: e.target.value,
                  })
                }
                className={inputClass + " resize-none"}
                rows={1}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-3 flex justify-center gap-2">
            <button
              onClick={saveVehicle}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Запис
            </button>
            <button
              onClick={getVehicleDataByRegistrationNumber}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
            <button
              onClick={getVehiclePoliciesByRegistrationNumber}
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Търси полици
            </button>
          </div>
        </div>
      </fieldset>

      <VehiclePoliciesDataGrid
        clientPoliciesList={vehiclePolicies}
        isLoading={isLoading}
      />
    </>
  );
};

export default VehicleData;
